<script lang="ts">
  import { onMount, onDestroy, createEventDispatcher } from 'svelte';
  import { updateUrlParams } from '$utils/updateUrlParams';
  import { browser } from '$app/environment';
  import { Search } from 'flowbite-svelte';
  import { searchQuery } from '$lib/stores';

  export let liveSearchEnabled: boolean = true;
  export let className: string = '';
  export let isEmbedded: boolean | undefined | null = false;
  export let searchLayout: string = 'inline';
  export let redirectUrl: string = '';
  export let searchParam: string = 'filter[s]'; // default param for search

  let searchTerm: string = '';
  let debounceTimer: ReturnType<typeof setTimeout>;

  const dispatch = createEventDispatcher();

  const unsubscribe = searchQuery.subscribe((value) => {
    searchTerm = value;
  });

  onDestroy(() => {
    unsubscribe();
  });

  // Debounce the search term
  $: if (liveSearchEnabled) {
    debounceSearch(searchTerm, 500);
  }

  onMount(() => {
    if (browser) {
      const searchParams = new URLSearchParams(window.location.search);
      searchTerm = searchParams.get(searchParam) || '';
      searchQuery.set(searchTerm);
    }
  });

  function debounceSearch(term: string, delay: number) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => navigateToSearch(term), delay);
  }

  function navigateToSearch(term: string = '') {
    if (browser) {
      const params = { [searchParam]: term };
      updateUrlParams(params, redirectUrl, false);
      searchQuery.set(term);
      dispatch('search');
    }
  }

  function clearSearch() {
    searchTerm = '';
    navigateToSearch('');
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !liveSearchEnabled) {
      navigateToSearch(searchTerm);
    }
  }
</script>

<div class="search-input {searchLayout ?? ''} {className} {isEmbedded ? 'iframe' : ''}">
  <div class="relative">
    <Search class={searchLayout} placeholder="Søg" size="sm" bind:value={searchTerm} on:keydown={handleKeyDown} name="search" autocomplete="off" svgClass="shrink-0 w-5 h-5" />
    {#if searchTerm}
      <button class="absolute inset-y-0 right-0 pr-3 flex items-center clearButton" on:click={clearSearch} aria-label="Clear search">
        <svg xmlns="http://www.w3.org/2000/svg" class="shrink-0 h-6 w-6 !block" role="img" aria-label="close outline" viewBox="0 0 24 24">
          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18 17.94 6M18 18 6.06 6" />
        </svg>
      </button>
    {/if}
  </div>

  {#if searchLayout === 'button'}
    <button class="search-button" on:click={() => navigateToSearch(searchTerm)}>
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="shrink-0 w-5 h-5" role="img" aria-label="search outline" viewBox="0 0 24 24">
        <path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
      </svg>
    </button>
  {/if}
</div>
